import { useCallback, useEffect, useState } from "react";
import { Loader2, Coins, Gift, Wallet as WalletIcon } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { getWallet, getHistory, listPacks, startTopUp, creditsLabel } from "@/lib/wallet";
import { WALLET } from "@/constants/testIds";

function BalanceCard({ icon: Icon, label, value, sub, gold = false, testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border p-5 ${gold ? "border-[color:rgba(212,175,55,0.32)] bg-[color:var(--rb-obsidian)]" : "border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)]"}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[color:var(--rb-text-faint)]">
        <Icon className={`h-4 w-4 ${gold ? "text-[color:var(--rb-gold)]" : "text-[color:var(--rb-accent)]"}`} /> {label}
      </div>
      <div className={`rb-display mt-2 text-3xl font-semibold ${gold ? "text-[color:var(--rb-gold)]" : "text-[color:var(--rb-text)]"}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-[color:var(--rb-text-faint)]">{sub}</div>}
    </div>
  );
}

export default function Wallet() {
  const [wallet, setWallet] = useState(null);
  const [packs, setPacks] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [w, p, h] = await Promise.all([getWallet(), listPacks(), getHistory()]);
      setWallet(w);
      setPacks(p);
      setHistory(h);
    } catch (e) {
      console.error("[wallet] load failed", e);
      toast.error("Failed to load your wallet");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const buy = async (packId) => {
    setBusy(packId);
    try {
      await startTopUp(packId); // redirects to Stripe
    } catch (e) {
      console.error("[wallet] top-up failed", e);
      toast.error("Could not start checkout. Please try again.");
      setBusy(null);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl">
        <h1 className="rb-display text-2xl font-semibold text-[color:var(--rb-text)]">Credits</h1>
        <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">
          1 credit = $1. Use credits to build skills and buy from the marketplace.
        </p>

        {loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--rb-accent)]" /></div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <BalanceCard testid={WALLET.balanceSpendable} icon={Coins} label="Spendable" value={creditsLabel(wallet?.spendable_cents)} sub="credits" gold />
              <BalanceCard testid={WALLET.balanceCash} icon={WalletIcon} label="Cashable" value={creditsLabel(wallet?.cashable_cents)} sub="credits you funded" />
              <BalanceCard testid={WALLET.balancePromo} icon={Gift} label="Promo" value={creditsLabel(wallet?.promo_cents)} sub="free, spend-only" />
            </div>

            <h2 className="rb-display mt-10 text-lg font-semibold text-[color:var(--rb-text)]">Top up</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {packs.map((pack) => (
                <div key={pack.id} className="rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5 text-center">
                  <div className="rb-display text-2xl font-semibold text-[color:var(--rb-text)]">{creditsLabel(pack.credits_cents)} credits</div>
                  <div className="mt-1 text-sm text-[color:var(--rb-text-faint)]">${(pack.price_cents / 100).toFixed(2)}</div>
                  <Button
                    data-testid={`${WALLET.topupPack}-${pack.id}`}
                    disabled={busy === pack.id}
                    onClick={() => buy(pack.id)}
                    className="mt-4 w-full rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]"
                  >
                    {busy === pack.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buy credits"}
                  </Button>
                </div>
              ))}
            </div>

            {history.length > 0 && (
              <>
                <h2 className="rb-display mt-10 text-lg font-semibold text-[color:var(--rb-text)]">Account history</h2>
                <div data-testid="wallet-history" className="mt-4 divide-y divide-[color:var(--rb-border)] rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)]">
                  {history.map((h) => {
                    const credit = h.delta_cents >= 0;
                    return (
                      <div key={h.id} className="flex items-center justify-between gap-3 px-5 py-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm text-[color:var(--rb-text)]">{h.label}</div>
                          <div className="text-xs text-[color:var(--rb-text-faint)]">
                            {h.created_at ? new Date(h.created_at).toLocaleDateString() : ""}
                            {h.bucket === "promo" ? " · promo" : ""}
                          </div>
                        </div>
                        <div className={`rb-mono shrink-0 text-sm ${credit ? "text-[color:var(--rb-success)]" : "text-[color:var(--rb-text-dim)]"}`}>
                          {credit ? "+" : "−"}{creditsLabel(Math.abs(h.delta_cents))} cr
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
